import React from 'react';
import type { ComponentProps } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type {
  BackgroundColorProps,
  BorderRadiusProps,
  FontWeightProps,
  JustifyContentProps,
  TextAlignProps,
} from 'styled-system';
import {
  backgroundColor,
  borderColor,
  borderRadius,
  color,
  fontWeight,
  justifyContent,
  size,
  textAlign,
  themeGet,
} from 'styled-system';

import { font } from '../../theme/helper';
import { ButtonBase, SPINNER_SIZE } from './Base';

const BG_HOVER = 'colors.sys.neutral.background.hover';

const COLOR_ACTIVE = 'colors.sys.neutral.text.active';
const BG_ACTIVE = 'colors.sys.neutral.background.active';

const COLOR_DISABLED = 'colors.sys.neutral.background.inverted';
const BORDER_DISABLED = 'colors.sys.neutral.background.inverted';

const UnstyledButtonSecondary = styled(ButtonBase)<
  FontWeightProps &
    TextAlignProps &
    JustifyContentProps &
    BackgroundColorProps &
    BorderRadiusProps & {
      // Added to satisfy TS and keep code without refactoring. To be replaced with properties.
      fontSize?: number;
      lineHeight?: number;
    } & {
      isDisabled?: boolean;
    }
>`
  ${color};
  ${fontWeight};
  ${textAlign};
  ${justifyContent};
  ${backgroundColor};
  ${borderRadius};
  ${props => font(props.fontSize || 3, props.lineHeight)(props)};

  [role='progressbar'] svg {
    ${({ spinnerSize }) => size((spinnerSize as number) || SPINNER_SIZE)};
    stroke: ${themeGet('colors.sys.neutral.background.inverted')};
  }

  cursor: ${({ isLoading, isDisabled }) =>
    isLoading ? 'progress' : isDisabled ? 'not-allowed' : 'pointer'};

  ${props =>
    !props.isDisabled
      ? css`
          &:hover,
          &:focus {
            border-style: solid;
            border-width: ${themeGet('borderWidths.small')(props)};
            border-color: ${themeGet('colors.sys.neutral.border.default')(props)};
            background-color: ${themeGet(BG_HOVER)(props)};
          }
          &:active {
            color: ${themeGet(COLOR_ACTIVE)(props)};
            background-color: ${themeGet(BG_ACTIVE)(props)};
            border-style: solid;
            border-width: ${themeGet('borderWidths.small')(props)};
            border-color: ${themeGet('colors.sys.neutral.border.strong')(props)};
          }
        `
      : css`
          color: ${themeGet(COLOR_DISABLED)(props)};
          border-color: ${themeGet(BORDER_DISABLED)(props)};
        `};
`;

export const ButtonSecondary = (props: ComponentProps<typeof UnstyledButtonSecondary>) => (
  <UnstyledButtonSecondary
    px="sp_16"
    py="sp_8"
    color="sys.neutral.border.strong"
    fontWeight="bold"
    textAlign="center"
    justifyContent="center"
    backgroundColor="sys.neutral.background.default"
    borderRadius="small"
    borderStyle="solid"
    borderWidth="small"
    borderColor="sys.neutral.border.hover"
    {...props}
  />
);

const UnstyledButtonSecondary_V2 = styled(ButtonSecondary)<any>`
  ${borderColor}

  ${props =>
    !props.isDisabled
      ? css`
          &:hover,
          &:focus {
            color: ${themeGet('colors.sys.neutral.text.hover')(props)};
            border-color: ${themeGet('colors.sys.neutral.border.hover')(props)};
            background-color: ${themeGet('colors.sys.neutral.background.hover')(props)};
          }

          &:active {
            color: ${themeGet('colors.sys.neutral.text.active')(props)};
            border-color: ${themeGet('colors.sys.neutral.border.active')(props)};
            background-color: ${themeGet('colors.sys.neutral.background.active')(props)};
          }
        `
      : css`
          color: ${themeGet('colors.sys.neutral.text.inactive')(props)};
          border-color: ${themeGet('colors.sys.neutral.border.inactive')(props)};
          background-color: ${themeGet('colors.sys.neutral.background.inactive')(props)};
        `};
`;

export const ButtonSecondary_V2 = (props: ComponentProps<typeof UnstyledButtonSecondary_V2>) => (
  <UnstyledButtonSecondary_V2
    color="sys.neutral.text.default"
    borderColor="sys.neutral.border.default"
    backgroundColor="sys.neutral.background.default"
    {...props}
  />
);
